import type { ServicePageConfig } from "../../types";
import {
  getPackagePricingRows,
  PACKAGE_PRICE_HINT_SV,
  PACKAGE_PRICE_HINT_EN,
} from "../../package-pricing";
import { getCityMunicipalityList } from "../../cities";

const goteborgKommunerText = getCityMunicipalityList("göteborg");

export const angeredPages: ServicePageConfig[] = [
  {
    slug: "bortforsling-angered",
    serviceCategory: "bortforsling",
    city: "göteborg",

    meta: {
      title: "Bortforsling Angered – Snabb Hämtning | Ungdomskraft",
      description:
        "Boka bortforsling i Angered. Vi hämtar möbler, vitvaror och skräp i Hjällbo, Hammarkullen, Gårdsten, Rannebergen, Lövgärdet och Bergum. Fast pris, ofta samma dag.",
      h1: "Bortforsling Angered",
    },

    hero: {
      heading: "Bortforsling i Angered",
      intro:
        "Boka bortforsling i Angered – vi hämtar möbler, vitvaror och skrymmande föremål i hela stadsdelen, från höghusen i Hjällbo, Hammarkullen och Gårdsten till villorna i Bergum, Olofstorp och Gunnilse. Snabb bokning, fast pris och ofta tillgängliga redan samma dag. Tusentals slutförda uppdrag i Göteborgsregionen.",
      heroImage: "/hero-bortforsling.webp",
      heroImageAlt:
        "Ungdomskraft hämtar möbler vid bortforsling i Angered – ung medarbetare lastar skåpbil",
    },

    itemsSection: {
      heading: "Saker vi bortforslar i Angered",
      items: [
        {
          name: "Återvinning",
          description: "Glas, metall, papper och annat återvinningsbart",
          image: "/recycling-bin.webp",
          alt: "Återvinning som hämtas vid bortforsling i Angered och körs till Kretsloppsparken Alelyckan",
        },
        {
          name: "Kartonger",
          description: "Flyttkartonger och wellpappkartonger",
          image: "/box.webp",
          alt: "Flyttkartonger som forslas bort från villa i Bergum, Angered",
        },
        {
          name: "Möbler",
          description: "Vardagsrum, sovrum, kontor och andra hemmöbler",
          image: "/furniture.webp",
          alt: "Soffor och hemmöbler vid bortforsling i Hjällbo, Angered",
        },
        {
          name: "Elektronik",
          description:
            "TV-apparater, datorer, ljudsystem och annan elektronik",
          image: "/electronics.webp",
          alt: "Elektronik och TV-apparater som bortforslas i Hammarkullen, Angered",
        },
        {
          name: "Vitvaror",
          description:
            "Kylskåp, tvättmaskiner, torktumlare och andra vitvaror",
          image: "/appliances.webp",
          alt: "Kylskåp och vitvaror som hämtas vid bortforsling i Gårdsten, Angered",
        },
        {
          name: "Träningsutrustning",
          description:
            "Löpband, cyklar, vikter och annan träningsutrustning",
          image: "/fitness-equipment.webp",
          alt: "Löpband och träningsutrustning vid bortforsling i Rannebergen, Angered",
        },
      ],
    },

    seoContent: {
      mainHeading: "Bortforsling i Angered – snabbt, smidigt och ansvarsfullt",
      mainText:
        "Angered är en av Göteborgs största stadsdelar, belägen i nordost och byggd som en satellitstad under miljonprogrammets år. Bebyggelsen är blandad: de stora bostadsområdena Hjällbo, Hammarkullen, Eriksbo, Rannebergen, Gårdsten och Lövgärdet med höghus och stora bostadsgårdar, samt det grönare och mer lantliga norra Angered med villor i Bergum, Olofstorp och Gunnilse längs Lärjeåns dalgång. Den blandningen gör bortforsling till olika utmaningar: i lägenheten handlar det ofta om hiss, trapphus och stora gårdar, medan det i villan snarare handlar om garage, förråd och tomter. Med Ungdomskraft slipper du allt krångel oavsett vilket. Vi skickar unga, starka medarbetare med skåpbil direkt till din adress.\n\nVårt team hanterar hela kedjan: vi bär ut föremålen från din lägenhet, villa eller ditt förråd, lastar i bilen och kör till närmaste återvinningscentral. För Angered är Kretsloppsparken Alelyckan närmast, vid Lärjeågatan – en återvinningscentral med både återbruk för byggmaterial och secondhandbutik. Du behöver inte tänka på sortering, parkering eller tunga lyft. Vi tar hand om allt.\n\nTack vare att Angeredsbanans snabbspårväg och E6 binder ihop stadsdelen med centrala Göteborg, och vi planerar rutten via Angeredsbron, tar vi oss snabbt mellan områdena. Det gör att vi ofta kan vara på plats redan samma dag du bokar. Våra medarbetare är vana vid både höghusen i Hjällbo och Gårdsten och de lantliga villagatorna i Bergum – och har utrustningen som krävs: bärbälten, skyddsfiltar och lastramper som skyddar både dina ägodelar och trapphuset under hela processen.",
      preparationHeading: "Förbered inför din bortforsling i Angered",
      preparationText:
        "Du behöver egentligen inte göra mycket alls – våra ungdomar sköter det tunga jobbet. Men vill du snabba på processen och hålla nere kostnaden finns det några enkla saker du kan göra:\n\nRensa en fri väg (ungefär 1 meter bred) från entrén till föremålen som ska bort. Bor du i villa i Bergum, Olofstorp eller Gunnilse är det smidigt om vi kan backa in på uppfarten – meddela oss om grinden eller garageporten behöver öppnas. Bor du i lägenhet i Hjällbo, Hammarkullen, Gårdsten, Rannebergen eller Lövgärdet, kontrollera om det finns en lastzon, innergård eller gästparkering nära porten som vi kan använda för lastning – i miljonprogrammets stora bostadsgårdar finns det ofta bra angöringsplatser.\n\nSamla gärna sakerna på ett ställe om möjligt, till exempel i hallen, garaget eller förrådet. Du behöver inte demontera möbler – det ingår i vår tjänst om det behövs. Farligt avfall som färgrester, batterier och kemikalier kan vi tyvärr inte ta med, men sådant lämnar du gratis som göteborgare på Kretsloppsparken Alelyckan.",
      additionalSections: [
        {
          heading: "Områden vi täcker i Angered",
          content:
            "Vi utför bortforsling i hela Angered – från de stora bostadsområdena till det lantliga norra Angered. Oavsett om du bor i ett höghus i Gårdsten, en lägenhet i Hjällbo, ett radhus i Rannebergen eller en villa i Bergum kan vi vara på plats, ofta redan samma dag.\n\nI de centrala delarna kring Angereds Centrum – med Hjällbo, Hammarkullen, Eriksbo, Rannebergen, Gårdsten och Lövgärdet – har vi stor vana vid lägenhetshämtningar med hiss och trapphus i miljonprogrammets hus, där det ofta finns rymliga gårdar och bra angöring för skåpbilen.\n\nVi är minst lika snabba i norra Angered: villaområdena och byarna i Bergum, Olofstorp, Gunnilse och Agnesberg längs Lärjeåns dalgång och nära Vättlefjälls naturreservat. Tack vare närheten till Angeredsbanan, E6 och Gråbovägen planerar vi alltid rutten i förväg så att du slipper vänta. Inga extra avgifter för något område.",
        },
        {
          heading: "Återvinning och miljöansvar i Angered",
          content:
            "En viktig del av vår bortforsling i Angered är att allt hanteras på ett miljövänligt sätt. Vi sorterar materialet och kör det till godkända återvinningscentraler. I Göteborg sköts avfallet av Kretslopp och vatten, och den anläggning vi använder mest för uppdrag i Angered är:\n\nKretsloppsparken Alelyckan vid Lärjeågatan – den närmaste stationen, en kretsloppspark som rymmer både återvinningscentral, ett återbruk för byggmaterial och en secondhandbutik. Här tas sorterat grovavfall, elavfall, vitvaror, förpackningar och farligt avfall emot. Eftersom Alelyckan ligger nära håller vi restiden – och därmed kostnaden – nere.\n\nDet som kan återanvändas skänker vi vidare till organisationer som Stadsmissionen och Emmaus. På så sätt bidrar din bortforsling till cirkulär ekonomi – sakerna får ett nytt liv istället för att hamna på tippen.\n\nOm sakerna transporteras till en secondhand-butik eller välgörenhetsorganisation för återanvändning kan du ha rätt till RUT-avdrag på arbetskostnaden. Vanlig bortforsling där allt körs till återvinningsstation ger inte rätt till avdrag. Avdraget hanteras direkt på fakturan.",
        },
        {
          heading: "Varför boka bortforsling i Angered med Ungdomskraft?",
          listItems: [
            {
              label: "Lokal kunskap –",
              text: "Vi känner Angered, från höghusen i Hjällbo, Hammarkullen och Gårdsten till villorna i Bergum och Olofstorp, och vet att Kretsloppsparken Alelyckan ligger nära. Det gör att vi planerar effektiva rutter och håller nere kostnaden för dig.",
            },
            {
              label: "Snabb tillgänglighet –",
              text: "Vi har skåpbilar och personal i hela Göteborgsregionen och når Angered via E6 och Angeredsbron. Ofta kan vi vara på plats redan samma dag du bokar – även på kvällar och helger.",
            },
            {
              label: "Fast pris utan överraskningar –",
              text: "Du ser priset direkt vid bokning. Inga dolda avgifter, inga tilläggsavgifter för något område i Angered, och inga offertförfrågningar. Boka på två minuter.",
            },
            {
              label: "Försäkrat och tryggt –",
              text: "Alla uppdrag är försäkrade genom Trygg Hansa. Ditt trapphus, din innergård, dina väggar och dina ägodelar är skyddade under hela processen.",
            },
            {
              label: "Miljövänlig hantering –",
              text: "Vi sorterar och återvinner allt vi kan via Kretsloppsparken Alelyckan. Det som går att donera skänks vidare. Du kan ha rätt till RUT-avdrag för transport till återbruk.",
            },
          ],
        },
      ],
    },

    faq: [
      {
        question: "Vad kostar bortforsling i Angered?",
        answer:
          "Priset beror på volym, vikt och tid. Vår minsta kategori (Extra Liten) startar på 249 kr i grundavgift, och ett genomsnittligt uppdrag kostar runt 499–2 495 kr beroende på mängden. Du ser det exakta priset direkt vid bokning – inga dolda avgifter och inga tillägg för något område i Angered.",
      },
      {
        question: "Kan jag få RUT-avdrag på bortforsling i Angered?",
        answer:
          "Om sakerna transporteras till en secondhand-butik eller välgörenhetsorganisation för återanvändning kan du ha rätt till RUT-avdrag på arbetskostnaden. Vanlig bortforsling där allt körs till återvinningsstation ger inte rätt till avdrag. Avdraget hanteras direkt på fakturan.",
      },
      {
        question: "Hur snabbt kan ni komma för bortforsling i Angered?",
        answer:
          "Vi har skåpbilar och personal i hela Göteborgsregionen och når Angered via E6 och Angeredsbron, så vi tar oss snabbt mellan områdena. Ofta kan vi vara på plats redan samma dag, och vid akuta behov ibland inom 60 minuter. Boka online och välj den tid som passar dig.",
      },
      {
        question: "Ingår det en skåpbil vid bortforsling i Angered?",
        answer:
          "Ja, vi kommer alltid med skåpbil och personal. Du behöver inte ordna eget fordon – vi sköter allt från bärning till transport och bortkörning till närmaste återvinningscentral.",
      },
      {
        question: "Kan ni bära ner saker från lägenhet i trappa i Angered?",
        answer:
          "Absolut. Vi är vana vid både hiss och trapphus i miljonprogrammets hus i Hjällbo, Hammarkullen, Gårdsten, Rannebergen och Lövgärdet. Vi bär ner möbler, vitvaror och annat oavsett våning – inga extra avgifter för trappor.",
      },
      {
        question: "Hämtar ni från villa och garage i Angered?",
        answer:
          "Ja, en stor del av våra uppdrag i norra Angered är villahämtningar i Bergum, Olofstorp och Gunnilse. Vi backar in på uppfarten där det går och tömmer garage, källare och förråd. Meddela oss bara om en grind eller garageport behöver öppnas.",
      },
      {
        question: "Kan ni demontera möbler innan bortforsling?",
        answer:
          "Ja, vi demonterar möbler på plats om det behövs för att få ut dem genom dörrar eller nedför trappor. Det ingår i tjänsten utan extra kostnad.",
      },
      {
        question: "Är bortforslingen i Angered försäkrad?",
        answer:
          "Ja, alla uppdrag är försäkrade genom Trygg Hansa. Det innebär att din fastighet, ditt trapphus, din innergård och dina ägodelar är skyddade under hela processen – från lyft till transport.",
      },
      {
        question: "Vart kör ni avfallet efter bortforsling i Angered?",
        answer:
          "Vi sorterar allt material och kör det till närmaste godkända återvinningscentral. För Angered är det oftast Kretsloppsparken Alelyckan vid Lärjeågatan, som drivs av Kretslopp och vatten och även har secondhandbutik. Det som kan återanvändas skänks till Stadsmissionen eller Emmaus.",
      },
      {
        question: "Kan jag boka bortforsling i Angered på helgen?",
        answer:
          "Ja, vi erbjuder bortforsling alla dagar i veckan, inklusive lördagar och söndagar. Välj det datum och den tid som passar dig bäst direkt i vår bokning.",
      },
      {
        question: "Kan ni hämta saker från källare eller förråd i Angered?",
        answer:
          "Ja, vi hämtar föremål från alla utrymmen – källare, förråd, vindsutrymmen och garage. Vi är vana vid både de gemensamma förrådsutrymmena i lägenhetshusen i Hjällbo och Gårdsten och villaförråden i Bergum.",
      },
      {
        question: "Kan ni göra flera stopp vid bortforsling i Angered?",
        answer:
          "Ja, du kan lägga till flera hämtadresser i samma bokning. Det är praktiskt om du till exempel behöver hämta saker från både din lägenhet i Hammarkullen och ditt förråd på en annan adress i Angered.",
      },
      {
        question: "Vilka områden i Angered täcker ni?",
        answer:
          "Vi utför bortforsling i hela Angered – Hjällbo, Hammarkullen, Eriksbo, Rannebergen, Gårdsten, Lövgärdet och Angereds Centrum samt norra Angered med Bergum, Olofstorp, Gunnilse och Agnesberg. Inga extra avgifter för något område.",
      },
      {
        question: "Tar ni hand om farligt avfall vid bortforsling i Angered?",
        answer:
          "Vi kan tyvärr inte transportera farligt avfall som färg, kemikalier, batterier eller asbest. Sådant lämnar du gratis som göteborgare på Kretsloppsparken Alelyckan.",
      },
      {
        question: "Vilka kommuner runt Angered utför ni bortforsling i?",
        answer: `Vi finns i hela Göteborgsregionen, inklusive följande kommuner: ${goteborgKommunerText}. Inga extra avgifter för kranskommuner.`,
      },
    ],

    priceHint: PACKAGE_PRICE_HINT_SV.bortforsling,

    pricingRows: getPackagePricingRows("bortforsling", "sv"),

    dynamicHeadings: {
      threeSteps: "Boka din bortforsling i Angered i 3 enkla steg",
      step2Text:
        "Våra ungdomar i Angered kommer till dig och hämtar det du vill ha bortforslat. Vi hanterar tunga lyft, trånga trapphus, smala hissar och fulla villagarage – du behöver inte lyfta ett finger.",
      ctaHeading: "Boka bortforsling i Angered idag",
      ctaText:
        "Fyll i formuläret och få pris direkt – inga offertförfrågningar. Smidig bortforsling i hela Angered.",
    },

    en: {
      slug: "junk-removal-angered",
      meta: {
        title: "Junk Removal Angered – Fast Pickup | Ungdomskraft",
        description:
          "Book junk removal in Angered. We pick up furniture, appliances and bulky items in Hjällbo, Hammarkullen, Gårdsten, Rannebergen, Lövgärdet and Bergum. Fixed price, often same day.",
        h1: "Junk Removal Angered",
      },
      hero: {
        heading: "Junk Removal in Angered",
        intro:
          "Book junk removal in Angered – we collect furniture, appliances and bulky items across the whole district, from the high-rises of Hjällbo, Hammarkullen and Gårdsten to the villas in Bergum, Olofstorp and Gunnilse. Quick booking, fixed price and often available the same day. Thousands of completed jobs in the Gothenburg region.",
        heroImageAlt:
          "Ungdomskraft collecting furniture during junk removal in Angered – young worker loading a van",
      },
      itemsSection: {
        heading: "Items we remove in Angered",
        items: [
          {
            name: "Recycling",
            description: "Glass, metal, paper and other recyclables",
            image: "/recycling-bin.webp",
            alt: "Recyclables collected during junk removal in Angered and driven to Kretsloppsparken Alelyckan",
          },
          {
            name: "Boxes",
            description: "Moving boxes and cardboard boxes",
            image: "/box.webp",
            alt: "Moving boxes removed from a villa in Bergum, Angered",
          },
          {
            name: "Furniture",
            description: "Living room, bedroom, office and other home furniture",
            image: "/furniture.webp",
            alt: "Sofas and home furniture during junk removal in Hjällbo, Angered",
          },
          {
            name: "Electronics",
            description: "TVs, computers, sound systems and other electronics",
            image: "/electronics.webp",
            alt: "Electronics and TVs removed in Hammarkullen, Angered",
          },
          {
            name: "Appliances",
            description: "Fridges, washing machines, dryers and other appliances",
            image: "/appliances.webp",
            alt: "Fridges and appliances collected during junk removal in Gårdsten, Angered",
          },
          {
            name: "Fitness Equipment",
            description: "Treadmills, bikes, weights and other exercise equipment",
            image: "/fitness-equipment.webp",
            alt: "Treadmill and fitness equipment during junk removal in Rannebergen, Angered",
          },
        ],
      },
      seoContent: {
        mainHeading: "Junk removal in Angered – fast, hassle-free and responsible",
        mainText:
          "Angered is one of Gothenburg's largest districts, located in the northeast and built as a satellite town during the 'miljonprogram' era. The housing is mixed: the large residential areas of Hjällbo, Hammarkullen, Eriksbo, Rannebergen, Gårdsten and Lövgärdet with high-rises and large courtyards, and the greener, more rural northern Angered with villas in Bergum, Olofstorp and Gunnilse along the Lärje river valley. That mix makes junk removal a different challenge: in an apartment it is often elevators, stairwells and large courtyards, while in a villa it is more about garages, storage rooms and plots. With Ungdomskraft, you skip the hassle either way. We send young, strong workers with a van directly to your address.\n\nOur team handles the entire chain: we carry items out of your apartment, villa or storage unit, load them into the van and drive to the nearest recycling centre. For Angered, Kretsloppsparken Alelyckan is the closest, on Lärjeågatan – a recycling park with both a reuse centre for building materials and a second-hand shop. You don't need to worry about sorting, parking or heavy lifting. We take care of everything.\n\nBecause the Angered rapid tram line and the E6 connect the district with central Gothenburg, and we plan the route via the Angered bridge, we move quickly between the areas. This means we can often be on site the same day you book. Our workers are used to both the high-rises of Hjällbo and Gårdsten and the rural villa streets of Bergum – and have the right equipment: carrying belts, protective blankets and loading ramps that protect both your belongings and the stairwell throughout the process.",
        preparationHeading: "Preparing for your junk removal in Angered",
        preparationText:
          "You don't really need to do much at all – our young workers handle the heavy work. But if you want to speed things up and keep the cost down, there are a few simple things you can do:\n\nClear a path (about 1 metre wide) from the entrance to the items that need to go. If you live in a villa in Bergum, Olofstorp or Gunnilse, it helps if we can reverse onto the driveway – let us know if a gate or garage door needs opening. If you live in an apartment in Hjällbo, Hammarkullen, Gårdsten, Rannebergen or Lövgärdet, check whether there is a loading zone, courtyard or visitor parking near the entrance we can use – the large courtyards of the 'miljonprogram' often have good spots to pull up the van.\n\nGather the items in one place if possible, for example in the hallway, garage or storage unit. You don't need to disassemble furniture – we include that in our service if needed. We cannot take hazardous waste such as paint, batteries and chemicals, but as a Gothenburg resident you can drop those off for free at Kretsloppsparken Alelyckan.",
        additionalSections: [
          {
            heading: "Areas we cover in Angered",
            content:
              "We perform junk removal across the whole of Angered – from the large residential areas to the rural northern Angered. Whether you live in a high-rise in Gårdsten, an apartment in Hjällbo, a townhouse in Rannebergen or a villa in Bergum, we can be on site, often the same day.\n\nIn the central parts around Angereds Centrum – with Hjällbo, Hammarkullen, Eriksbo, Rannebergen, Gårdsten and Lövgärdet – we are highly experienced with apartment pickups via elevator and stairwell in the 'miljonprogram' buildings, where there are often spacious courtyards and good access for the van.\n\nWe are just as quick in northern Angered: the villa areas and villages of Bergum, Olofstorp, Gunnilse and Agnesberg along the Lärje river valley and near the Vättlefjäll nature reserve. Thanks to the proximity of the Angered rapid tram, the E6 and Gråbovägen, we always plan the route in advance so you don't have to wait. No extra charges for any area.",
          },
          {
            heading: "Recycling and environmental responsibility in Angered",
            content:
              "An important part of our junk removal in Angered is that everything is handled in an environmentally responsible way. We sort the materials and drive them to approved recycling centres. In Gothenburg, waste is handled by Kretslopp och vatten, and the facility we use most for jobs in Angered is:\n\nKretsloppsparken Alelyckan on Lärjeågatan – the closest station, a recycling park that houses a recycling centre, a reuse centre for building materials and a second-hand shop. It receives sorted bulky waste, e-waste, white goods, packaging and hazardous waste. Because Alelyckan is close by, we keep travel time – and therefore the cost – down.\n\nItems that can be reused are donated to organisations like Stadsmissionen and Emmaus. This way, your junk removal contributes to a circular economy – items get a new life instead of ending up in landfill.\n\nIf the items are transported to a charity shop or organisation for reuse, you may be eligible for the Swedish RUT tax deduction on the labour cost. Standard junk removal where everything goes to a recycling station does not qualify. The deduction is handled directly on the invoice.",
          },
          {
            heading: "Why book junk removal in Angered with Ungdomskraft?",
            listItems: [
              {
                label: "Local knowledge –",
                text: "We know Angered, from the high-rises of Hjällbo, Hammarkullen and Gårdsten to the villas of Bergum and Olofstorp, and that Kretsloppsparken Alelyckan is close by. This lets us plan efficient routes and keep the cost down for you.",
              },
              {
                label: "Fast availability –",
                text: "We have vans and staff across the Gothenburg region and reach Angered via the E6 and the Angered bridge. We can often be on site the same day you book – including evenings and weekends.",
              },
              {
                label: "Fixed price with no surprises –",
                text: "You see the price immediately when booking. No hidden fees, no surcharges for any area in Angered, and no quote requests. Book in two minutes.",
              },
              {
                label: "Insured and safe –",
                text: "All jobs are insured through Trygg Hansa. Your stairwell, your courtyard, your walls and your belongings are protected throughout the entire process.",
              },
              {
                label: "Eco-friendly handling –",
                text: "We sort and recycle everything we can via Kretsloppsparken Alelyckan. Items suitable for donation are passed on. You may be entitled to a RUT tax deduction for transport to reuse.",
              },
            ],
          },
        ],
      },
      faq: [
        {
          question: "How much does junk removal in Angered cost?",
          answer:
            "The price depends on volume, weight and time. Our smallest category (Extra Liten) starts at 249 kr base fare, and an average job costs around 499–1,699 kr depending on the amount. You see the exact price immediately when booking – no hidden fees and no surcharges for any area in Angered.",
        },
        {
          question: "Can I get a RUT tax deduction on junk removal in Angered?",
          answer:
            "If the items are transported to a charity shop or organisation for reuse, you may be eligible for the Swedish RUT tax deduction on the labour cost. Standard junk removal where everything goes to a recycling station does not qualify. The deduction is handled directly on the invoice.",
        },
        {
          question: "How quickly can you come for junk removal in Angered?",
          answer:
            "We have vans and staff across the Gothenburg region and reach Angered via the E6 and the Angered bridge, so we move quickly between the areas. We can often be on site the same day, and for urgent needs sometimes within 60 minutes. Book online and choose the time that suits you.",
        },
        {
          question: "Is a van included with junk removal in Angered?",
          answer:
            "Yes, we always come with a van and crew. You don't need to arrange your own vehicle – we handle everything from carrying to transport and disposal at the nearest recycling centre.",
        },
        {
          question: "Can you carry items down from an apartment via stairs in Angered?",
          answer:
            "Absolutely. We are used to both elevators and stairwells in the 'miljonprogram' buildings in Hjällbo, Hammarkullen, Gårdsten, Rannebergen and Lövgärdet. We carry furniture, appliances and other items regardless of floor – no extra charges for stairs.",
        },
        {
          question: "Do you collect from villas and garages in Angered?",
          answer:
            "Yes, a large share of our jobs in northern Angered are villa pickups in Bergum, Olofstorp and Gunnilse. We reverse onto the driveway where possible and clear garages, basements and storage rooms. Just let us know if a gate or garage door needs opening.",
        },
        {
          question: "Can you disassemble furniture before removal?",
          answer:
            "Yes, we disassemble furniture on site if needed to get it through doors or down stairs. This is included in the service at no extra cost.",
        },
        {
          question: "Is junk removal in Angered insured?",
          answer:
            "Yes, all jobs are insured through Trygg Hansa. This means your property, stairwell, courtyard and belongings are protected throughout the entire process – from lifting to transport.",
        },
        {
          question: "Where do you take the waste after junk removal in Angered?",
          answer:
            "We sort all materials and drive them to the nearest approved recycling centre. For Angered that is usually Kretsloppsparken Alelyckan on Lärjeågatan, run by Kretslopp och vatten, which also has a second-hand shop. Items that can be reused are donated to Stadsmissionen or Emmaus.",
        },
        {
          question: "Can I book junk removal in Angered on weekends?",
          answer:
            "Yes, we offer junk removal every day of the week, including Saturdays and Sundays. Choose the date and time that works best for you directly in our booking.",
        },
        {
          question: "Can you collect items from basements or storage units in Angered?",
          answer:
            "Yes, we pick up items from all spaces – basements, storage rooms, attics and garages. We are used to both the shared storage rooms in the apartment buildings in Hjällbo and Gårdsten and the villa storage rooms in Bergum.",
        },
        {
          question: "Can you make multiple stops during junk removal in Angered?",
          answer:
            "Yes, you can add multiple pickup addresses in the same booking. This is practical if you need to collect items from both your apartment in Hammarkullen and a storage unit at another address in Angered, for example.",
        },
        {
          question: "Which areas in Angered do you cover?",
          answer:
            "We perform junk removal across all of Angered – Hjällbo, Hammarkullen, Eriksbo, Rannebergen, Gårdsten, Lövgärdet and Angereds Centrum, as well as northern Angered with Bergum, Olofstorp, Gunnilse and Agnesberg. No extra charges for any area.",
        },
        {
          question: "Do you handle hazardous waste during junk removal in Angered?",
          answer:
            "Unfortunately, we cannot transport hazardous waste such as paint, chemicals, batteries or asbestos. As a Gothenburg resident you can drop those off for free at Kretsloppsparken Alelyckan.",
        },
        {
          question: "Which municipalities around Angered do you serve?",
          answer:
            "We operate across the entire Gothenburg region, including Ale, Alingsås, Göteborg, Härryda, Kungälv, Kungsbacka, Lerum, Lilla Edet, Mölndal, Partille, Stenungsund, Tjörn and Öckerö. No extra charges for surrounding municipalities.",
        },
      ],
      priceHint: PACKAGE_PRICE_HINT_EN.bortforsling,
      pricingRows: getPackagePricingRows("bortforsling", "en"),
      dynamicHeadings: {
        threeSteps: "Book your junk removal in Angered in 3 easy steps",
        step2Text:
          "Our young workers in Angered come to you and collect whatever you want removed. We handle heavy lifting, tight staircases, narrow elevators and full villa garages – you don't have to lift a finger.",
        ctaHeading: "Book junk removal in Angered today",
        ctaText:
          "Fill in the form and get an instant price – no quote requests. Easy junk removal across the whole of Angered.",
      },
    },
  },
];
